import jsPDF from "jspdf";
import { loadLogoForPdf } from "../convocations/logoLoader";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

/**
 * Richiesta di certificato di idoneità sportiva (agonistica / non agonistica)
 * indirizzata al medico curante o pediatra di libera scelta o medico dello sport.
 */
export async function downloadMedicalRequestPdf({ team, player, request, signature }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 50;
  let y = margin;

  // Header with logo
  const logo = await loadLogoForPdf(team);
  if (logo) {
    try { doc.addImage(logo.dataUrl, logo.format, margin, y, 60, 60); } catch (e) { console.warn(e); }
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(team?.legal_name || team?.name || "", pageW - margin, y + 10, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80);
  if (team?.address) doc.text(team.address, pageW - margin, y + 24, { align: "right" });
  if (team?.fiscal_code) doc.text(`C.F./P.IVA ${team.fiscal_code}`, pageW - margin, y + 36, { align: "right" });
  if (team?.figc_affiliation_code) doc.text(`Matricola FIGC ${team.figc_affiliation_code}`, pageW - margin, y + 48, { align: "right" });

  y += 90;
  doc.setTextColor(20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Luogo e data: ${team?.address ? team.address.split(",").pop().trim() + ", " : ""}${fmtDate(new Date().toISOString())}`, pageW - margin, y, { align: "right" });

  y += 30;
  doc.setFontSize(11);
  doc.text("Egregio/a Dott./Dott.ssa", margin, y);
  y += 16;
  doc.setFont("helvetica", "bold");
  doc.text(request?.doctor_name || "Medico curante / Pediatra / Medico dello Sport", margin, y);
  y += 30;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  const oggetto = request?.visit_type === "agonistica"
    ? "Oggetto: Richiesta di certificato di idoneità all'attività sportiva AGONISTICA"
    : "Oggetto: Richiesta di certificato di idoneità all'attività sportiva NON AGONISTICA";
  const oggettoLines = doc.splitTextToSize(oggetto, pageW - margin * 2);
  doc.text(oggettoLines, margin, y);
  y += 18 * oggettoLines.length + 8;

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  const body = `La presente per richiedere il rilascio del certificato di idoneità ${
    request?.visit_type === "agonistica" ? "all'attività sportiva agonistica" : "alla pratica sportiva non agonistica"
  } per il minore di seguito indicato, tesserato/tesseranda presso la nostra società per la stagione sportiva ${team?.season || ""}, categoria ${team?.category || ""}, disciplina CALCIO.`;
  const bodyLines = doc.splitTextToSize(body, pageW - margin * 2);
  doc.text(bodyLines, margin, y, { lineHeightFactor: 1.4 });
  y += 14 * bodyLines.length + 14;

  // Player info box
  doc.setDrawColor(150);
  doc.setLineWidth(0.5);
  doc.rect(margin, y, pageW - margin * 2, 80);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("DATI DEL TESSERATO", margin + 10, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Cognome e Nome: ${player?.last_name || ""} ${player?.first_name || ""}`, margin + 10, y + 30);
  doc.text(`Data di nascita: ${fmtDate(player?.birth_date)}`, margin + 10, y + 46);
  doc.text(`Codice Fiscale: ${player?.fiscal_code || "________________"}`, margin + 10, y + 62);
  doc.text(`Categoria: ${team?.category || ""}`, pageW - margin - 180, y + 30);
  doc.text(`Ruolo: ${player?.role || ""}`, pageW - margin - 180, y + 46);
  y += 100;

  if (request?.appointment_date || request?.appointment_place) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Appuntamento fissato:", margin, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    if (request.appointment_date) doc.text(`Data: ${fmtDate(request.appointment_date)}`, margin, y);
    if (request.appointment_place) doc.text(`Luogo: ${request.appointment_place}`, margin + 250, y);
    y += 22;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const closing = `Ringraziando anticipatamente per la collaborazione, si rimane a disposizione per ogni necessità.

Cordiali saluti.`;
  const closeLines = doc.splitTextToSize(closing, pageW - margin * 2);
  doc.text(closeLines, margin, y, { lineHeightFactor: 1.4 });
  y += 14 * closeLines.length + 20;

  // Signature
  doc.setDrawColor(120);
  doc.line(pageW - margin - 220, y + 26, pageW - margin, y + 26);
  doc.setFontSize(8);
  doc.text("Timbro e firma del Presidente / Legale rappresentante", pageW - margin - 220, y + 38);
  doc.text(team?.president_name || "", pageW - margin - 220, y + 52);

  // Footer
  const pageH = doc.internal.pageSize.getHeight();
  if (signature) {
    doc.setDrawColor(16, 185, 129);
    doc.setFillColor(236, 253, 245);
    doc.roundedRect(margin, pageH - 70, pageW - margin * 2, 36, 4, 4, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(6, 95, 70);
    doc.text("FIRMATO DIGITALMENTE VIA OTP EMAIL", margin + 10, pageH - 58);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      `Firmatario: ${signature.signer_name || signature.signer_email} · ${signature.signer_email}`,
      margin + 10, pageH - 46,
    );
    doc.text(
      `Firmato il ${new Date(signature.signed_at).toLocaleString("it-IT")} · ID firma: ${signature.id}`,
      margin + 10, pageH - 36,
    );
  }
  doc.setFontSize(7);
  doc.setTextColor(140);
  doc.text(`${team?.name || ""} · Documento generato il ${new Date().toLocaleString("it-IT")}`, pageW / 2, pageH - 20, { align: "center" });

  const safe = `richiesta_visita_${(player?.last_name || "x")}`.toLowerCase().replace(/[^a-z0-9_]/g, "_");
  doc.save(`${safe}.pdf`);
}
